import axios, {
	type AxiosError,
	type AxiosRequestConfig,
	type AxiosResponse,
} from 'axios'

import AuthService from '../authService'
import {
	getLocalAccessToken,
	getLocalRefreshToken,
	updateLocalUser,
} from '../tokenService'

const token = getLocalAccessToken()

const instance = axios.create({
	baseURL: `${process.env.REACT_APP_CMS_API_HOST}`,
	headers: {
		Authorization: `Bearer ${token}`,
	},
})

let isRefreshing = false

type FailedRequest = {
	resolve: (token: string) => void
	reject: (error: AxiosError) => void
}

let failedQueue: FailedRequest[] = []

const processQueue = (error: AxiosError | null, token: string | null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error)
		} else if (token) {
			prom.resolve(token)
		}
	})

	failedQueue = []
}

instance.interceptors.response.use(
	res => {
		return res
	},
	async (err: AxiosError) => {
		const originalConfig = err.config as AxiosRequestConfig & {
			_retry?: boolean
		}

		if (err.response?.status === 401 && !originalConfig._retry) {
			if (isRefreshing) {
				return new Promise<AxiosResponse>((resolve, reject) => {
					failedQueue.push({
						resolve: (token: string) => {
							if (originalConfig.headers) {
								originalConfig.headers.Authorization = `Bearer ${token}`
							}
							resolve(instance(originalConfig))
						},
						reject,
					})
				})
			}

			originalConfig._retry = true
			isRefreshing = true

			try {
				const refresh_token = getLocalRefreshToken()

				if (!refresh_token) {
					window.location.href = '/signin'
					return Promise.reject(err)
				}

				const user = await AuthService.refreshToken(refresh_token)
				const newAccessToken = user.access_token
				updateLocalUser(user)

				instance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`
				processQueue(null, newAccessToken)

				if (originalConfig.headers) {
					originalConfig.headers.Authorization = `Bearer ${newAccessToken}`
				}

				return instance(originalConfig)
			} catch (err) {
				processQueue(err as AxiosError, null)
				return Promise.reject(err)
			} finally {
				isRefreshing = false
			}
		}

		return Promise.reject(err)
	}
)

export default instance
